import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Brain, ArrowLeft, Scale, Zap, BookOpen, ExternalLink, Lightbulb, Target, Sparkles, Users, Trophy, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const HowItWorks = () => {
  const navigate = useNavigate();
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      id: 'think-lab',
      title: 'Think Lab',
      subtitle: 'AI-Powered Brainstorming Workspace',
      color: 'from-purple-600 to-pink-600',
      icon: Lightbulb,
      description: 'Your personal AI brainstorming companion for structured creative thinking',
      keyFeatures: [
        'Persona-based AI guidance (10+ expert personas)',
        'Stage-by-stage thinking framework',
        'Real-time OI & MSA scoring',
        'Save and revisit ideas anytime'
      ],
      stats: [
        { label: 'Personas Available', value: '10+' },
        { label: 'Thinking Stages', value: '6' }
      ],
      cta: 'Launch Think Lab',
      route: '/dashboard?thinklab=open'
    },
    {
      id: 'impact-lab',
      title: 'Impact Lab',
      subtitle: 'IDEO Design Thinking Sprints',
      color: 'from-emerald-600 to-teal-600',
      icon: Target,
      description: '7-day structured innovation sprints using IDEO methodology with AI-powered analysis',
      keyFeatures: [
        'GPT-5 secondary research & precedents',
        'Suggested team personas & expertise',
        '7-day IDEO roadmap (Frame → Share)',
        'Curated resources & Think Lab exercises'
      ],
      stats: [
        { label: 'Sprint Duration', value: '7 Days' },
        { label: 'Active Labs', value: '150+' }
      ],
      cta: 'Explore Impact Labs',
      route: '/challenges'
    },
    {
      id: 'challenges',
      title: 'Social Innovation Challenges',
      subtitle: 'UN SDG-Aligned Global Problems',
      color: 'from-green-600 to-emerald-600',
      icon: Trophy,
      description: 'Tackle real-world challenges from poverty to climate action with a global community',
      keyFeatures: [
        '27+ challenges aligned with UN SDGs',
        'Collaborate with 1000+ innovators',
        'Discussion boards & submission galleries',
        'Launch Impact Labs for any challenge'
      ],
      stats: [
        { label: 'Global Challenges', value: '27+' },
        { label: 'Active Innovators', value: '1K+' }
      ],
      cta: 'Browse Challenges',
      route: '/challenges'
    },
    {
      id: 'evaluate',
      title: 'Quick Evaluate',
      subtitle: 'Instant Paper Assessment',
      color: 'from-blue-600 to-cyan-600',
      icon: FileText,
      description: 'Upload your paper and get detailed OI scores with specific feedback on each dimension',
      keyFeatures: [
        'Upload PDF, DOCX, or TXT files',
        'GPT-5 deep analysis of content',
        'Detailed dimension-by-dimension feedback',
        'Strengths, weaknesses, and improvements'
      ],
      stats: [
        { label: 'Supported Formats', value: '3' },
        { label: 'Analysis Time', value: '<2min' }
      ],
      cta: 'Try Quick Evaluate',
      route: '/quick-evaluate'
    }
  ];

  const nextFeature = () => {
    setCurrentFeature((prev) => (prev + 1) % features.length);
  };

  const prevFeature = () => {
    setCurrentFeature((prev) => (prev - 1 + features.length) % features.length);
  };

  useEffect(() => {
    const interval = setInterval(nextFeature, 6000);
    return () => clearInterval(interval);
  }, []);

  const currentData = features[currentFeature];
  const Icon = currentData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              Idea Fountain
            </h1>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="border-purple-200 hover:bg-purple-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              How Does <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">IF</span> Work?
            </h2>
            <p className="text-xl text-slate-600">
              A scientific framework for measuring and enabling originality in the AI age
            </p>
          </div>

          {/* Originality Index Details */}
          <Card className="mb-8 border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl text-purple-700">
                <Scale className="w-6 h-6" />
                Originality Index (OI)
              </CardTitle>
              <CardDescription className="text-base">
                Four-dimensional measure with weighted scoring
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h3 className="font-bold text-lg mb-2 text-slate-800">Formula:</h3>
                <p className="font-mono text-purple-700 bg-purple-50 p-3 rounded">
                  OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical Anchoring)
                </p>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-slate-800 mb-1">Novelty (30%)</h4>
                  <p className="text-slate-600 text-sm">
                    Semantic distance, rarity of combination, originality of form/expression, disruptive potential
                  </p>
                  <div className="mt-2 text-xs text-slate-500">
                    <strong>Scale:</strong> 0-2 Derivative | 3-4 Incremental | 5-6 Fresh Variation | 7-8 Strongly Original | 9-10 Breakthrough
                  </div>
                </div>

                <div className="border-l-4 border-pink-500 pl-4">
                  <h4 className="font-bold text-slate-800 mb-1">Resonance (30%)</h4>
                  <p className="text-slate-600 text-sm">
                    Emotional engagement, cultural relevance, accessibility, virality
                  </p>
                  <div className="mt-2 text-xs text-slate-500">
                    <strong>Scale:</strong> 0-2 Ignored | 3-4 Niche Appeal | 5-6 Moderate Interest | 7-8 Strong Connection | 9-10 Cultural Shift
                  </div>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-bold text-slate-800 mb-1">Synthesis (20%)</h4>
                  <p className="text-slate-600 text-sm">
                    Cross-disciplinary integration, cross-cultural integration, depth of connection, generativity
                  </p>
                  <div className="mt-2 text-xs text-slate-500">
                    <strong>Scale:</strong> 0-2 Fragmented | 3-4 Weak Links | 5-6 Moderate Integration | 7-8 Strong Synthesis | 9-10 Elegant Fusion
                  </div>
                </div>

                <div className="border-l-4 border-amber-500 pl-4">
                  <h4 className="font-bold text-slate-800 mb-1">Ethical Anchoring (20%)</h4>
                  <p className="text-slate-600 text-sm">
                    Attribution, inclusivity, fairness, cultural respect
                  </p>
                  <div className="mt-2 text-xs text-slate-500">
                    <strong>Scale:</strong> 0-2 Harmful | 3-4 Questionable | 5-6 Neutral | 7-8 Responsible | 9-10 Deeply Ethical
                  </div>
                </div>
              </div>

              <div className="bg-purple-100 p-4 rounded-lg">
                <h3 className="font-bold text-purple-800 mb-2">Interpretation Thresholds:</h3>
                <div className="space-y-1 text-sm text-purple-900">
                  <div><strong>9-10:</strong> Exceptional originality</div>
                  <div><strong>8-8.9:</strong> Very high originality</div>
                  <div><strong>6-7.9:</strong> Moderate originality</div>
                  <div><strong>Below 6:</strong> Low originality</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mirror-Spark Assessment Details */}
          <Card className="mb-8 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl text-emerald-700">
                <Zap className="w-6 h-6" />
                Mirror-Spark Assessment (MSA)
              </CardTitle>
              <CardDescription className="text-base">
                Distinguish human creativity from AI mimicry
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-slate-800 mb-3">Mirror Indicators (0-10)</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Replication (0-3):</strong> Degree of copying/reproduction</div>
                    <div><strong>Trend Alignment (0-2):</strong> Following current patterns</div>
                    <div><strong>Conformity (0-3):</strong> Adherence to norms</div>
                    <div><strong>Surface Innovation (0-2):</strong> Shallow novelty</div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h3 className="font-bold text-emerald-800 mb-3">Spark Indicators (0-10)</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Novel Insight (0-3):</strong> Genuinely new perspective</div>
                    <div><strong>Intuitive Leap (0-2):</strong> Beyond logical steps</div>
                    <div><strong>Cultural Resonance (0-3):</strong> Deep cultural connection</div>
                    <div><strong>Generativity (0-2):</strong> Spawns further ideas</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-emerald-200">
                <h3 className="font-bold text-lg mb-2 text-slate-800">Calculation:</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Mirror % = (Mirror Score / (Mirror + Spark)) × 100
                </p>
                <p className="text-sm text-slate-600">
                  Spark % = (Spark Score / (Mirror + Spark)) × 100
                </p>
              </div>

              <div className="bg-emerald-100 p-4 rounded-lg">
                <h3 className="font-bold text-emerald-800 mb-2">Interpretation:</h3>
                <div className="space-y-1 text-sm text-emerald-900">
                  <div><strong>0-19% Mirror:</strong> Exceptional Spark (Human creativity signal)</div>
                  <div><strong>20-39% Mirror:</strong> Strong Spark</div>
                  <div><strong>40-59% Mirror:</strong> Hybrid Originality</div>
                  <div><strong>60-79% Mirror:</strong> Safe/Conventional</div>
                  <div><strong>80-100% Mirror:</strong> Derivative</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Research Papers */}
          <Card className="mb-8 border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl text-indigo-700">
                <BookOpen className="w-6 h-6" />
                Read the Research
              </CardTitle>
              <CardDescription className="text-base">
                Original academic papers on the Originality Enabling System
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700 mb-4">
                The IF framework is based on peer-reviewed research that establishes a new standard 
                for assessing originality in the AI age. Read the complete white papers:
              </p>
              
              <div className="space-y-3">
                <a 
                  href="http://dx.doi.org/10.2139/ssrn.5411122" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-md transition-all duration-300 group"
                >
                  <ExternalLink className="w-5 h-5 text-indigo-600 group-hover:text-indigo-700" />
                  <div>
                    <div className="font-semibold text-slate-800 group-hover:text-indigo-700">
                      Originality Framework Paper 1
                    </div>
                    <div className="text-sm text-slate-600">
                      DOI: 10.2139/ssrn.5411122
                    </div>
                  </div>
                </a>

                <a 
                  href="http://dx.doi.org/10.2139/ssrn.5411082" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-md transition-all duration-300 group"
                >
                  <ExternalLink className="w-5 h-5 text-indigo-600 group-hover:text-indigo-700" />
                  <div>
                    <div className="font-semibold text-slate-800 group-hover:text-indigo-700">
                      Originality Framework Paper 2
                    </div>
                    <div className="text-sm text-slate-600">
                      DOI: 10.2139/ssrn.5411082
                    </div>
                  </div>
                </a>
              </div>

              <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-sm text-indigo-900">
                  <strong>Citation:</strong> Priyanka Jha (2025). "Originality Enabling System: 
                  A Framework for Assessing and Amplifying Human Creativity in the AI Age"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Feature Showcase Carousel */}
          <div className="my-16">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-slate-900 mb-2">Key Features in Action</h3>
              <p className="text-lg text-slate-600">See how IF powers your creative journey</p>
            </div>

            <div className="relative">
              {/* Hero Feature Card */}
              <Card className="relative overflow-hidden border-2 shadow-2xl">
                {/* Gradient Header - Left Aligned */}
                <div className={`h-24 bg-gradient-to-r ${currentData.color} relative`}>
                  <div className="absolute inset-0 bg-black/10"></div>
                  <div className="absolute inset-0 flex items-center px-8">
                    <div className="flex items-center gap-6">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-white">
                        <h3 className="text-3xl font-bold drop-shadow-lg leading-tight">{currentData.title}</h3>
                        <p className="text-white/90 text-sm mt-1">{currentData.subtitle}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3 text-lg">What It Does</h4>
                        <p className="text-slate-600 leading-relaxed">{currentData.description}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3 text-lg">Key Features</h4>
                        <ul className="space-y-2">
                          {currentData.keyFeatures.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-slate-600">
                              <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-1" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        {currentData.stats.map((stat, idx) => (
                          <Card key={idx} className="border-2 border-purple-100">
                            <CardContent className="p-4 text-center">
                              <div className="text-3xl font-bold text-purple-600">{stat.value}</div>
                              <div className="text-xs text-slate-600 mt-1">{stat.label}</div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>

                      <Button 
                        className={`w-full h-14 text-lg font-semibold bg-gradient-to-r ${currentData.color} hover:opacity-90 transition-opacity`}
                        onClick={() => navigate(currentData.route)}
                      >
                        {currentData.cta}
                      </Button>

                      <p className="text-sm text-slate-500 text-center">
                        Part of the Originality Enabling System
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation Arrows */}
              <button
                onClick={prevFeature}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
              >
                <ChevronLeft className="w-6 h-6 text-slate-700" />
              </button>
              <button
                onClick={nextFeature}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
              >
                <ChevronRight className="w-6 h-6 text-slate-700" />
              </button>

              {/* Dot Indicators */}
              <div className="flex justify-center gap-2 mt-6">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentFeature(index)}
                    className={`transition-all ${
                      index === currentFeature 
                        ? 'w-8 h-3 bg-purple-600' 
                        : 'w-3 h-3 bg-slate-300 hover:bg-slate-400'
                    } rounded-full`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 hover:from-purple-700 hover:via-pink-700 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => navigate('/session/new')}
            >
              Start Your IF Session
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <AppFooter className="bg-gradient-to-r from-purple-50 to-pink-50 border-t-slate-200">
        <p className="text-xs text-slate-500 mt-3">
          <span className="font-medium">Privacy:</span> <span>OI: Opt-in</span> • <span>IQI: Always Public</span> • <span>MSA: Opt-in</span>
        </p>
      </AppFooter>
    </div>
  );
};

export default HowItWorks;
